/**
 * Impersonation banner (A2).
 *
 * Persistent red banner shown across /app/* whenever a super_admin is
 * impersonating a user. The impersonation JWT (held in the impersonation
 * store) carries an `impersonatedBy` claim; we render the banner only when an
 * active impersonation is present AND its token genuinely carries that claim.
 *
 * "Leave impersonation" calls POST /v1/superadmin/impersonate/stop on the
 * operator's own session (useOperatorSession), clears the store, drops the
 * impersonated query cache, and returns the operator to the /superadmin
 * surface.
 */

import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useImpersonation, useImpersonationControls } from '../lib/acting/context';
import { useStopImpersonation } from '../lib/api/superadmin';
import { displayPersonName } from '../lib/format/person-name';

/**
 * Mounted on every /app/* page. The common (not-impersonating) path reads only
 * the acting context's impersonation view — NO router/query context — so the
 * layout renders without a QueryClientProvider/Router when idle. The
 * context-dependent hooks live in the inner component, which mounts ONLY while
 * an impersonation is active.
 *
 * ARC-01: the banner no longer reads the store or decodes the token itself;
 * `useImpersonation()` is null unless the stored credential genuinely carries
 * an `impersonatedBy` claim (the same defensive guard, now in one place).
 */
export function ImpersonationBanner() {
  const impersonation = useImpersonation();
  if (!impersonation) return null;
  return <ActiveImpersonationBanner subjectName={impersonation.subjectName} />;
}

function ActiveImpersonationBanner({ subjectName }: { subjectName: string }) {
  const stop = useStopImpersonation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { endImpersonation: clearImpersonation } = useImpersonationControls();

  function handleLeave() {
    stop.mutate(undefined, {
      onSuccess: () => {
        // Order matters: drop the target's cached queries + return to the
        // operator console BEFORE clearing the store. Clearing the store
        // unmounts this banner, and react-query skips a per-call onSuccess if
        // the calling component has already unmounted — so the navigate must
        // run first. (The stop hook itself no longer auto-clears for the same
        // reason.)
        qc.clear();
        void navigate({ to: '/superadmin' });
        clearImpersonation();
      },
    });
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="impersonation-banner"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        flexWrap: 'wrap',
        padding: '8px 16px',
        background: 'var(--negative, #b53b3b)',
        color: '#fff',
        fontSize: 13.5,
        fontWeight: 500,
        boxShadow: '0 1px 0 rgba(0,0,0,0.15)',
      }}
    >
      <span>
        Impersonating <strong>{displayPersonName(subjectName)}</strong>. Every action is recorded against both you and this user.
      </span>
      <button
        type="button"
        onClick={handleLeave}
        disabled={stop.isPending}
        style={{
          background: '#fff',
          color: 'var(--negative, #b53b3b)',
          border: 'none',
          borderRadius: 6,
          padding: '4px 12px',
          fontSize: 12.5,
          fontWeight: 600,
          cursor: stop.isPending ? 'default' : 'pointer',
          opacity: stop.isPending ? 0.7 : 1,
        }}
      >
        {stop.isPending ? 'Leaving…' : 'Leave impersonation'}
      </button>
    </div>
  );
}
